/**
 * UsageStats - 워크스페이스 사용량 통계
 * 
 * Firestore Path: /workspaces/{wsId}/usage_stats/current
 * 
 * Cloud Functions에서 업데이트, 클라이언트에서 읽기만 가능
 */

export interface UsageStats {
    // 저장소 사용량
    storageBytes: number;          // 현재 사용 중인 저장소 (bytes)
    storageCount: number;          // 파일 개수

    // 월간 다운로드/전송량
    monthlyDownloadBytes: number;  // 이번 달 다운로드 총량
    monthlyUploadBytes: number;    // 이번 달 업로드 총량

    // 리소스 수
    memberCount: number;           // 현재 멤버 수
    channelCount: number;          // 채널 수
    botCount: number;              // 봇 수
    messageCount: number;          // 총 메시지 수

    // 메타데이터
    lastUpdatedAt: Date;
    monthlyResetAt: Date;          // 월간 카운터 리셋 시점
}

export interface UsageLimit {
    storageBytes: number;          // 최대 저장소
    monthlyDownloadBytes: number;  // 월간 다운로드 제한
    memberCount: number;           // 최대 멤버 수
    channelCount: number;          // 최대 채널 수
    botCount: number;              // 최대 봇 수
}

export interface UsageCheckResult {
    withinLimits: boolean;
    violations: UsageViolation[];
    usage: UsageStats;
    limits: UsageLimit;
}

export interface UsageViolation {
    type: 'storage' | 'download' | 'member' | 'channel' | 'bot';
    current: number;
    limit: number;
    percentage: number;            // 사용률 (%)
}

// 사용률 퍼센티지 계산
export function calculateUsagePercentage(current: number, limit: number): number {
    if (limit === 0) return 0; // 무제한
    return Math.round((current / limit) * 100);
}

// 용량 포맷팅
export function formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}

// 기본 UsageStats
export const defaultUsageStats = (): UsageStats => ({
    storageBytes: 0,
    storageCount: 0,
    monthlyDownloadBytes: 0,
    monthlyUploadBytes: 0,
    memberCount: 0,
    channelCount: 0,
    botCount: 0,
    messageCount: 0,
    lastUpdatedAt: new Date(),
    monthlyResetAt: new Date(),
});

// 제한 없음 (무제한)
export const UNLIMITED = 0;
